import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { syncSentryRoute } from "@/lib/sentryRoute";

/**
 * Issue #1529 — mirrors the current route onto Sentry's scope so an event's
 * `transaction` names the page the user was actually on. Renders nothing.
 *
 * The mechanism it works around (an SDK guard that skips the first navigation
 * when the destination URL is a prefix of the landing URL) is documented in
 * full in `@/lib/sentryRoute`. Read that before changing or removing this.
 *
 * ── Why a component and not a call inside AuthCallback ─────────────────────
 * The frozen name is not specific to the auth callback — that route is just
 * the one flow every new user passes through, so it is where the bug shows up.
 * Any deep landing path that redirects to `/` hits the same guard. Mounting
 * once above the routes covers all of them, including ones added later, and
 * keeps `AuthCallback` free of observability code it should not have to carry.
 *
 * ── Why it mirrors ScrollToTop's shape ────────────────────────────────────
 * Same job, same trigger: react to a location change from above `<Routes>`.
 * `useLocation` re-renders this component on every navigation, `replace` ones
 * included, which is precisely the case the SDK drops.
 *
 * ── Why it also fires on mount ────────────────────────────────────────────
 * Redundant with the pageload span TODAY (that path sets the name correctly),
 * and kept anyway: it makes the component's contract "the scope always names
 * the current route" rather than "the scope is correct provided something else
 * got the first one right". The write is idempotent and off the render path.
 */
const SentryRouteSync = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    syncSentryRoute(pathname);
  }, [pathname]);

  return null;
};

export default SentryRouteSync;
